import axios from 'axios';

//const SUBS_URL = 'http://localhost:5002/api';

const SUBS_URL = process.env.REACT_APP_SUBS_URL || 'http://localhost:5002/api';


const subsApi = axios.create({
    baseURL: SUBS_URL,
    timeout: 10000,
    headers: { 'Content-Type': 'application/json' }
});

subsApi.interceptors.request.use(config => {
    const token = localStorage.getItem('sub_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

export interface Plan {
    id: number;
    name: string;
    description: string;
    price: number;
    searchLimit: number;
    durationDays: number;
    isFree: boolean;
    features: string[];
    badgeText?: string;
    color: string;
    displayOrder: number;
}

export interface SubCheckResponse {
    allowed: boolean;
    reason: string;
    message: string;
    searchesUsed: number;
    searchLimit: number;
    currentPlan?: string;
    planExpiry?: string;
}

export interface CurrentSubDto {
    planName: string;
    planPrice: number;
    searchLimit: number;
    searchesUsed: number;
    searchesRemaining: number;
    startDate: string;
    endDate: string;
    isActive: boolean;
    isExpired: boolean;
}

export interface SearchLogItem {
    id: number;
    searchTerm: string;
    searchedAt: string;
    ipAddress: string;
}

export interface HistoryResponse {
    logs: SearchLogItem[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

export interface PaymentHistoryItem {
    id: number;
    amount: number;
    status: string;
    createdAt: string;
    stripePaymentIntentId: string;
}

export const subscriptionApi = {
    register: async (fullName: string, email: string, password: string) => {
        const res = await subsApi.post('/auth/register', { fullName, email, password });
        return res.data;
    },
    login: async (email: string, password: string) => {
        const res = await subsApi.post('/auth/login', { email, password });
        return res.data;
    },
    getMe: async () => {
        const res = await subsApi.get('/auth/me');
        return res.data;
    },
    updateProfile: async (fullName: string, email: string) => {
        const res = await subsApi.put('/auth/update-profile', { fullName, email });
        return res.data;
    },
    changePassword: async (currentPassword: string, newPassword: string) => {
        const res = await subsApi.put('/auth/change-password', { currentPassword, newPassword });
        return res.data;
    },
    logoutAll: async () => {
        const res = await subsApi.post('/auth/logout-all');
        return res.data;
    },
    deleteAccount: async (email: string, password: string) => {
        const res = await subsApi.delete('/auth/delete-account', { data: { email, password } });
        return res.data;
    },
    getPlans: async (): Promise<Plan[]> => {
        const res = await subsApi.get('/plans');
        return res.data;
    },
    getGuestCount: async (): Promise<SubCheckResponse> => {
        const res = await subsApi.get('/subscription/guest-count');
        return res.data;
    },
    getCurrentSubscription: async (): Promise<CurrentSubDto | null> => {
        try {
            const res = await subsApi.get('/subscription/current');
            return res.data;
        } catch { return null; }
    },
    cancelSubscription: async () => {
        const res = await subsApi.post('/subscription/cancel');
        return res.data;
    },
    getSearchHistory: async (page = 1, pageSize = 10): Promise<HistoryResponse> => {
        const res = await subsApi.get('/subscription/history', { params: { page, pageSize } });
        return res.data;
    },
    getAllHistory: async (): Promise<SearchLogItem[]> => {
        const res = await subsApi.get('/subscription/history/all');
        return res.data;
    },
    getPaymentHistory: async (): Promise<PaymentHistoryItem[]> => {
        const res = await subsApi.get('/payment/history');
        return res.data;
    },
    createPaymentIntent: async (planId: number) => {
        const res = await subsApi.post('/payment/create-intent', { planId });
        return res.data;
    },
    confirmPayment: async (paymentIntentId: string, planId: number) => {
        const res = await subsApi.post('/payment/confirm', { paymentIntentId, planId });
        return res.data;
    }
};